import type { DocNode } from '@atlaskit/adf-schema/doc';
import { getEmptyADF } from '@atlaskit/adf-utils/empty-adf';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { parseAdf } from '@atlassian/help-center-common-util/adf';
import type { Field } from '@atlassian/ui-modifications-core/types/field';
import type { FieldInternalMetadata } from '@atlassian/ui-modifications-core/types/field-internal-metadata';
import type { ViewType } from '@atlassian/ui-modifications-core/types/context';
import { isValidADF } from '@atlassian/ui-modifications-core/utils/is-valid-adf';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

export type TextAreaFieldPublicValue = string | DocNode;

/**
 * Field renderer constants
 */
export const ATLASSIAN_WIKI_RENDERER = 'atlassian-wiki-renderer';

/**
 * Validates that the text area value is appropriate for the field renderer
 */
export const validateTextAreaValue = (
	value: unknown,
	{ fieldRenderer }: FieldInternalMetadata,
	_createAnalyticsEvent: CreateUIAnalyticsEvent,
	extra: { currentField: Field; viewType: ViewType },
): Promise<boolean> => {
	if (fieldRenderer === ATLASSIAN_WIKI_RENDERER) {
		return isValidADF({
			newValue: value,
			currentValue: extra.currentField?.value,
		});
	}
	return Promise.resolve(typeof value === 'string');
};

/**
 * Transforms internal text area value to public format
 */
export const textAreaGetValueTransformer = (
	value: string,
	fieldRenderer?: string,
): TextAreaFieldPublicValue => {
	if (value === undefined || (FeatureGates.checkGate('ditto_fix_getvalue_empty_state') && !value)) {
		return fieldRenderer === ATLASSIAN_WIKI_RENDERER ? getEmptyADF() : '';
	}

	try {
		return parseAdf(value);
	} catch (e) {
		return value;
	}
};

/**
 * Transforms public text area value to internal format
 */
export const textAreaSetValueTransformer = (value: TextAreaFieldPublicValue): string => {
	// Check if value is string, if not convert it to string
	if (typeof value === 'string') {
		return value;
	}
	return JSON.stringify(value);
};
